import type { OnDestroy } from '@angular/core';
import type { AbstractControl, ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import type { FilePickerOptions } from '@commons/bo-ui/form/file-picker/models/file-picker-options.interface';
import type { FileResolvable, FileUrlOrProgress } from '@commons/bo-ui/form/file-picker/models/file-picker.types';
import type { FileWebServiceInterface } from '@commons/bo-ui/form/file-picker/services/file.web-service.interface';
import type { Observable, Subscription } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CropDialogComponent } from '@commons/bo-ui/form/file-picker/components/crop-dialog/crop-dialog.component';
import { SpinnerComponent } from '@commons/bo-ui/form/file-picker/components/spinner/spinner.component';
import { AcceptEnum } from '@commons/bo-ui/form/file-picker/models/accept.enum';
import { FileSizePipe } from '@commons/bo-ui/form/file-picker/pipes/file-size.pipe';
import { FILE_PICKER_SERVICE_TOKEN } from '@commons/bo-ui/form/file-picker/services/file-picker-service.token';
import { downloadFile } from '@commons/bo-ui/form/file-picker/utils/dowload-file';
import { BehaviorSubject, catchError, filter, noop, of, switchMap, tap } from 'rxjs';

type FileUploaderAbstractControl = Omit<AbstractControl, 'value'> & {
  value: FileResolvable;
};

@Component({
  selector: 'app-file-picker-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FileUploaderComponent),
      multi: true,
    },
  ],
  imports: [SpinnerComponent, MatIconButton, AsyncPipe, FileSizePipe],
})
export class FileUploaderComponent implements ControlValueAccessor, Validator, OnDestroy {
  @Input() options!: Partial<FilePickerOptions>;
  @Output() fileCancelled = new EventEmitter<void>();

  #file!: File | Blob | null;
  #url!: string;
  #fileUploadSubscription: Subscription | undefined;

  readonly #cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  readonly #fileWebservice: FileWebServiceInterface = inject(FILE_PICKER_SERVICE_TOKEN);
  readonly #dialog: MatDialog = inject(MatDialog);

  get url(): string {
    return this.#url;
  }

  set url(url: string) {
    this.#url = url;
    this.onChange(url);
    this.onTouched();
  }

  get file(): File | Blob | null {
    return this.#file;
  }

  set file(fileLike: File | Blob | null) {
    this.#file = fileLike;
    this.fileSize$.next(fileLike?.size);
  }

  get fileDisplayName(): string {
    return this.file instanceof File ? this.file.name : this.url;
  }

  downloadError = false;

  uploadProgress$ = new BehaviorSubject<number>(0);
  fileSize$ = new BehaviorSubject<number | undefined>(undefined);

  onChange: (url: string) => void = noop;
  onTouched: () => void = noop;

  ngOnDestroy(): void {
    this.#fileUploadSubscription?.unsubscribe();
    this.uploadProgress$.complete();
  }

  writeValue(fileResolvable: FileResolvable): void {
    if (fileResolvable === null) {
      return;
    }

    if (typeof fileResolvable !== 'string') {
      this.file = fileResolvable;
      this.#uploadFile(fileResolvable);
    } else {
      this.#fileWebservice
        .get(fileResolvable)
        .pipe(catchError(() => of(null)))
        .subscribe((file) => {
          this.file = file;
          this.downloadError = !file;
          this.url = fileResolvable;
          this.#cdr.detectChanges();
        });
    }
  }

  registerOnChange(onChangeFn: (url: string) => void): void {
    this.onChange = (url) => {
      onChangeFn(url);
      this.#cdr.markForCheck();
    };
    this.#cdr.detectChanges();
  }

  registerOnTouched(onTouchedFn: () => void): void {
    this.onTouched = () => {
      onTouchedFn();
      this.#cdr.markForCheck();
    };
    this.#cdr.detectChanges();
  }

  validate(control: FileUploaderAbstractControl): ValidationErrors | null {
    const isInProgress = typeof control.value !== 'string';
    const hasDownloadError = this.downloadError;
    return isInProgress || hasDownloadError ? { inProgress: isInProgress, downloadError: hasDownloadError } : null;
  }

  downloadUploadedFile(): void {
    downloadFile(this.file!);
  }

  #uploadFile(file: File): void {
    const uploadFile: (file: File) => Observable<FileUrlOrProgress> = (file) =>
      this.#fileWebservice.post(
        file,
        this.options.publicUpload,
        {
          additionalAllowedApps: this.options.additionalAllowedApps,
          additionalAllowedRoles: this.options.additionalAllowedRoles,
          additionalAllowedUsers: this.options.additionalAllowedUsers,
        },
        this.options.applySvgCurrentColor
      );
    const isFileCroppable = [AcceptEnum.JPG, AcceptEnum.PNG].includes(file.type as AcceptEnum);
    const fileUpload$: Observable<FileUrlOrProgress> =
      !this.options.multiUpload && this.options.enableCropper && isFileCroppable
        ? this.#openImageCropper(file, uploadFile)
        : uploadFile(file);

    this.#fileUploadSubscription = fileUpload$.subscribe({
      next: (urlOrProgress: FileUrlOrProgress) => {
        if (typeof urlOrProgress === 'number') {
          this.uploadProgress$.next(urlOrProgress);
        } else {
          this.url = urlOrProgress;
        }
      },
    });
  }

  #openImageCropper(
    file: File,
    uploadFileFn: (file: File) => Observable<FileUrlOrProgress>
  ): Observable<FileUrlOrProgress> {
    return this.#dialog
      .open(CropDialogComponent, {
        data: {
          image: file,
          name: file.name,
          cropperOptions: this.options.cropperOptions,
        },
      })
      .afterClosed()
      .pipe(
        tap((fileLike: File | null) => {
          if (!fileLike) {
            this.fileCancelled.emit();
          }
        }),
        filter((fileLike: File | null) => !!fileLike),
        switchMap(uploadFileFn)
      );
  }
}
